import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import api from "../lib/api";
import SEO, { breadcrumbSchema, articleSchema } from "../components/SEO";
import Breadcrumb from "../components/Breadcrumb";

export default function ComplianceDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(() => setError("Article not found"));
  }, [slug]);

  if (error) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="blog-not-found">
      <p className="text-slate-500">{error}</p>
      <Link to="/compliance-hub" className="text-[#1FA59A] font-semibold mt-4 inline-block">← Back to Compliance Hub</Link>
    </div>;
  }

  if (!post) return <div className="flex justify-center py-32"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Compliance Hub", path: "/compliance-hub" },
    { name: post.title, path: `/compliance-hub/${slug}` },
  ];

  return (
    <div data-testid={`page-blog-${slug}`}>
      <SEO
        title={`${post.title} | Compliance Hub · Oxford Environmental Consultants`}
        description={post.excerpt}
        path={`/compliance-hub/${slug}`}
        image={post.cover_image}
        schema={[
          breadcrumbSchema(crumbs),
          articleSchema({
            headline: post.title,
            description: post.excerpt,
            slug,
            image: post.cover_image,
            datePublished: post.created_at,
            dateModified: post.updated_at,
          }),
        ]}
      />
      <section className="bg-[#0A0F1C] text-white pt-16 pb-24 lg:pt-24 lg:pb-32">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <div className="mb-8"><Breadcrumb items={crumbs} theme="dark" /></div>
          <Link to="/compliance-hub" className="text-white/50 hover:text-white text-sm inline-flex items-center gap-2 mb-10" data-testid="back-to-hub">
            <ArrowLeft className="w-4 h-4" /> Back to Compliance Hub
          </Link>
          <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 mb-5">{post.category}</p>
          <h1 className="font-display text-4xl lg:text-6xl font-bold tracking-tight leading-[1.1]">{post.title}</h1>
          <p className="mt-8 text-lg text-white/70 leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      {post.cover_image && (
        <div className="max-w-5xl mx-auto px-6 lg:px-10 -mt-16 lg:-mt-24">
          <img
            src={post.cover_image}
            alt=""
            loading="eager"
            fetchPriority="high"
            decoding="async"
            className="w-full aspect-[21/9] object-cover border border-slate-200"
            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
          />
        </div>
      )}

      <article className="max-w-3xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="prose prose-slate prose-lg max-w-none
          prose-headings:font-display prose-headings:tracking-tight
          prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
          prose-p:text-slate-600 prose-p:leading-relaxed
          prose-ul:text-slate-600 prose-li:my-1
          prose-strong:text-slate-900"
          dangerouslySetInnerHTML={{ __html: post.content }} />
      </article>

      <section className="bg-[#F8FAFC] py-16 border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Need support on this topic?</h2>
          <Link to="/contact" className="gs-gradient text-white font-bold px-8 py-4 mt-6 inline-block hover:opacity-90" data-testid="blog-cta-contact">
            Speak to a consultant
          </Link>
        </div>
      </section>
    </div>
  );
}
